import { useCallback, useEffect, useRef, useState } from 'react';
import { applyAction, newMatch } from '@/engine/engine';
import { chooseBotAction, BOT_DELAY_MS } from '@/engine/bot';
import type { Action, GameEvent, GameState, MatchOptions } from '@/engine/types';
import { sounds } from '@/audio/sounds';
import { storage } from '@/lib/storage';
import { startPracticeTrace, recordPracticeStep, savePracticeTrace } from '@/lib/practiceLuckTrace';

/**
 * Wraps the pure GameEngine in a React-friendly hook.
 *
 * - state: current GameState
 * - dispatch(action): apply an action, emit events
 * - lastEvents: events emitted by the most recent action (for animation/sfx triggers)
 * - bot is driven by side-effect (timer) when it's the bot's turn.
 */
export function useGameEngine(initialOpts: MatchOptions = {}) {
  const [state, setState] = useState<GameState>(() => newMatch(initialOpts));
  const [lastEvents, setLastEvents] = useState<GameEvent[]>([]);
  const stateRef = useRef(state);
  stateRef.current = state;

  const traceRef = useRef<any>(null);

  const dispatch = useCallback((action: Action) => {
    const before = stateRef.current;
    if (!traceRef.current || traceRef.current.engineMatchId !== before.matchId) {
      traceRef.current = startPracticeTrace(before);
    }
    const result = applyAction(before, action);
    recordPracticeStep(traceRef.current, result.state, result.events);
    if (result.state.endState) savePracticeTrace(traceRef.current, result.state);
    setState(result.state);
    setLastEvents(result.events);
    // Audio side-effects.
    //   - "draw", "place", "attackWin" fire on the engine event (immediate UX feedback).
    //   - "goal", "save", "joker", "victory", "defeat" are intentionally NOT played here;
    //     they are triggered by their visual reveal components (ShotSequence,
    //     JokerSequence, MatchSummaryView) so the audio is synchronised with the
    //     on-screen reveal text.
    for (const ev of result.events) {
      if (ev.type === 'CARD_DRAWN') sounds.draw();
      else if (ev.type === 'CARD_PLACED') sounds.place();
      else if (ev.type === 'ATTACK_RESULT' && ev.outcome === 'win') sounds.attackWin();
    }
    return result;
  }, []);

  // Persist match result once
  const persistedRef = useRef(false);
  useEffect(() => {
    if (state.phase === 'finished' || (state.phase === 'match-end' && state.endState)) {
      if (!persistedRef.current && state.endState) {
        persistedRef.current = true;
        storage.recordMatch({
          winner: state.endState.winner,
          p1Goals: state.players.p1.score,
          p2Goals: state.players.p2.score,
          p1Saves: state.players.p1.savesPerformed,
          p2Saves: state.players.p2.savesPerformed,
        });
      }
    }
  }, [state.phase, state.endState, state.players.p1.score, state.players.p2.score, state.players.p1.savesPerformed, state.players.p2.savesPerformed]);

  const reset = useCallback((opts: MatchOptions = {}) => {
    persistedRef.current = false;
    setState(newMatch(opts));
    setLastEvents([]);
  }, []);

  return { state, dispatch, reset, lastEvents } as const;
}

export { BOT_DELAY_MS, chooseBotAction };
